import { trackAdEvent } from '@/lib/adPixels';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Loader2, Package, Truck, ShieldCheck, ArrowRight } from 'lucide-react';
import DeliveryInfoForm from '@/components/buyer/DeliveryInfoForm';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';


export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orderId, setOrderId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accountCreated, setAccountCreated] = useState(false);
  const [orderTotal, setOrderTotal] = useState<number | null>(null);
  const [needsDeliveryInfo, setNeedsDeliveryInfo] = useState(false);

  useEffect(() => {
    initializePaymentSuccess();
  }, []);

  const createBuyerAccount = async (orderIdToLink: string) => {
    const pendingEmail = sessionStorage.getItem('pending_buyer_email');
    const pendingPassword = sessionStorage.getItem('pending_buyer_password');
    const pendingName = sessionStorage.getItem('pending_buyer_name');
    const pendingPhone = sessionStorage.getItem('pending_buyer_phone');

    if (!pendingEmail || !pendingPassword) return false;

    try {
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: pendingEmail,
        password: pendingPassword,
        options: {
          data: {
            full_name: pendingName || '',
            phone: pendingPhone || '',
          },
          emailRedirectTo: window.location.origin,
        },
      });

      if (signUpError) {
        console.error('Signup error:', signUpError);
        if (signUpError.message?.includes('already registered') || signUpError.message?.includes('already been registered')) {
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email: pendingEmail,
            password: pendingPassword,
          });
          
          if (signInError) {
            console.warn('Could not sign in existing user:', signInError.message);
            toast.info('An account with this email already exists. Please log in to view your order.');
            return false;
          }
          
          if (signInData.user) {
            const { data: baseOrder } = await supabase
              .from('orders')
              .select('payment_reference')
              .eq('id', orderIdToLink)
              .maybeSingle();

            const paymentReference = baseOrder?.payment_reference || null;

            if (paymentReference) {
              await supabase
                .from('orders')
                .update({ buyer_id: signInData.user.id })
                .eq('payment_reference', paymentReference)
                .is('buyer_id', null);
            } else {
              await supabase
                .from('orders')
                .update({ buyer_id: signInData.user.id })
                .eq('id', orderIdToLink)
                .is('buyer_id', null);
            }

            await supabase.from('user_roles').insert({
              user_id: signInData.user.id,
              role: 'buyer',
            }).select().maybeSingle();
            
            return true;
          }
        }
        return false;
      }

      if (signUpData.user) {
        await supabase
          .from('profiles')
          .update({
            full_name: pendingName || '',
            phone: pendingPhone || '',
          })
          .eq('id', signUpData.user.id);

        await supabase.from('user_roles').insert({
          user_id: signUpData.user.id,
          role: 'buyer',
        }).select().maybeSingle();

        const { data: baseOrder } = await supabase
          .from('orders')
          .select('payment_reference')
          .eq('id', orderIdToLink)
          .maybeSingle();

        const paymentReference = baseOrder?.payment_reference || null;

        if (paymentReference) {
          await supabase
            .from('orders')
            .update({ buyer_id: signUpData.user.id })
            .eq('payment_reference', paymentReference)
            .is('buyer_id', null);
        } else {
          await supabase
            .from('orders')
            .update({ buyer_id: signUpData.user.id })
            .eq('id', orderIdToLink)
            .is('buyer_id', null);
        }

        return true;
      }
    } catch (error) {
      console.error('Error creating buyer account:', error);
    } finally {
      sessionStorage.removeItem('pending_buyer_email');
      sessionStorage.removeItem('pending_buyer_password');
      sessionStorage.removeItem('pending_buyer_name');
      sessionStorage.removeItem('pending_buyer_phone');
    }

    return false;
  };

  const initializePaymentSuccess = async () => {
    try {
      const orderIdFromUrl = searchParams.get('orderId');
      
      if (!orderIdFromUrl) {
        toast.error('Order ID not found. Please contact support.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      setOrderId(orderIdFromUrl);
      
      const fetchOrder = async () => {
        return supabase
          .from('orders')
          .select('shipping_address, delivery_address, recipient_name, recipient_phone, buyer_id, payment_status, payment_reference, status, total_amount')
          .eq('id', orderIdFromUrl)
          .single();
      };

      let { data: order, error: orderError } = await fetchOrder();

      if (orderError) {
        console.error('Error fetching order:', orderError);
        toast.error('Order not found. Please contact support.');
        setIsLoading(false);
        return;
      }

      // Fallback: client-side payment verification
      if (order?.payment_status !== 'completed' && order?.payment_reference) {
        try {
          const verifyRes = await supabase.functions.invoke('verify-payment', {
            body: { reference: order.payment_reference }
          });

          if (verifyRes.error) {
            console.error('verify-payment error:', verifyRes.error);
            toast.error('Payment verification failed. Please contact support.');
            setIsLoading(false);
            return;
          }

          ({ data: order, error: orderError } = await fetchOrder());
          if (orderError) {
            console.error('Error re-fetching order after verification:', orderError);
          }
        } catch (e) {
          console.error('Error calling verify-payment:', e);
          toast.error('Payment verification failed. Please contact support.');
          setIsLoading(false);
          return;
        }
      }

      if (order?.payment_status !== 'completed') {
        toast.error('Payment not confirmed yet. Please refresh in a moment.');
        setIsLoading(false);
        return;
      }

      setOrderTotal(order.total_amount);
      trackAdEvent('Purchase', { value: Number(order.total_amount) || 0 }, order.payment_reference || orderIdFromUrl);

      // Auto-create buyer account if credentials were stored
      const hasPendingAccount = sessionStorage.getItem('pending_buyer_email');
      if (hasPendingAccount) {
        const created = await createBuyerAccount(orderIdFromUrl);
        setAccountCreated(created);
        if (created) {
          toast.success('Account created successfully! Welcome to SabiTradeNG!');
        }
      }

      // Check if delivery info was already collected
      const hasDeliveryInfo = order.shipping_address || 
        (order.delivery_address && order.recipient_name && order.recipient_phone);

      if (!hasDeliveryInfo) {
        setNeedsDeliveryInfo(true);
        setIsLoading(false);
        return;
      }

      // Delivery info already provided — show success and let user navigate
      setIsLoading(false);
    } catch (error) {
      console.error('Error initializing payment success:', error);
      toast.error('Error processing payment. Please contact support.');
      setIsLoading(false);
    }
  };

  const handleDeliveryComplete = () => {
    toast.success('Order confirmed! Your delivery is being processed.');
    navigate('/buyer/orders');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <div>
              <h2 className="text-xl font-bold mb-2">Verifying Payment</h2>
              <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (orderId && needsDeliveryInfo) {
    return (
      <div className="min-h-screen bg-gradient-subtle py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6 text-center">
            <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Payment Successful!</h1>
            <p className="text-muted-foreground">
              {accountCreated 
                ? 'Your account has been created! Please provide your delivery information to complete your order.'
                : 'Please provide your delivery information to complete your order.'}
            </p>
          </div>
          
          <DeliveryInfoForm 
            orderId={orderId} 
            onComplete={handleDeliveryComplete}
          />
        </div>
      </div>
    );
  }

  if (orderId) {
    return (
      <div className="min-h-screen bg-gradient-subtle py-12 px-4">
        <div className="max-w-lg mx-auto">
          {/* Success animation */}
          <div className="text-center mb-8">
            <div className="h-20 w-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-in zoom-in-50 duration-500">
              <CheckCircle className="h-12 w-12 text-primary" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Payment Successful!</h1>
            {orderTotal && (
              <p className="text-lg text-primary font-semibold">₦{orderTotal.toLocaleString()}</p>
            )}
            {accountCreated && (
              <Badge variant="secondary" className="mt-2">Account created</Badge>
            )}
          </div>

          {/* What happens next */}
          <Card className="mb-6">
            <div className="p-6 space-y-4">
              <h3 className="font-semibold text-base">What happens next</h3>
              <div className="space-y-3">
                {[
                  { icon: Package, text: 'Seller will prepare your order', color: 'text-primary' },
                  { icon: Truck, text: 'You\'ll receive delivery tracking updates', color: 'text-primary' },
                  { icon: ShieldCheck, text: 'Your payment is held safely in escrow until delivery', color: 'text-primary' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <step.icon className={`h-4 w-4 ${step.color}`} />
                    </div>
                    <span className="text-sm">{step.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="space-y-3">
            <Button className="w-full" size="lg" onClick={() => navigate('/buyer/orders')}>
              View My Orders
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button variant="outline" className="w-full" size="lg" onClick={() => navigate('/products')}>
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
